import { createPortal } from 'react-dom';
import {
  FC,
  useEffect,
  useState,
} from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { SectionComponentInterface } from 'types/componentTypes';
import { Cross } from 'src/shared/ui/Cross';
import { NormalizedNcOffer } from 'src/shared/lib/offers/offersTypes';
import { Store } from 'app-redux/types/storeTypes';

import styles from './DynamicModalWithStore.module.scss';
import { OfferDetailsSection } from './OfferDetailsSection';
import { LegalTermsSection } from './LegalTerms';
import { ButtonSection } from './ButtonSection';
import { HeadingSection } from './HeadingSection';

import { parseBlocks, parseModalBlocks } from '../lib';
import { BlockType } from '../config/constants';
import { useDynamicOrDefaultBlock } from '../lib/hooks';

const DynamicModalWithStore: FC<SectionComponentInterface> = ({
  blocks,
  bgColor,
  extraData = {},
}) => {
  const {
    isOpenInitially = false,
    isModal = true,
    allowedBlocks = [BlockType.DEFAULT, BlockType.DYNAMIC],
  } = extraData;
  const [isOpen, setIsOpen] = useState<boolean>(isOpenInitially);
  const { defaultBlock, dynamicBlock, closeHandler, openHandler } = parseBlocks(blocks);
  // eslint-disable-next-line max-len
  const dynamicData = (useSelector((state: Store) => state.server.page.data?.offer) || null) as NormalizedNcOffer | null;

  const block = useDynamicOrDefaultBlock(
    defaultBlock,
    dynamicBlock,
    allowedBlocks,
    dynamicData,
  );

  useEffect(() => {
    if (!openHandler?.fullText) {
      return;
    }

    try {
      // eslint-disable-next-line no-eval
      const openFunction = eval(`(${openHandler.fullText})`);

      openFunction(setIsOpen);

      return () => {
        openFunction(setIsOpen);
      };
    } catch (error) {
      console.error(error);
    }
  }, [openHandler]);

  useEffect(() => {
    if (isOpenInitially) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpenInitially]);

  if (!block) {
    return null;
  }

  const parsedBlocks = parseModalBlocks(block.contentTypesCollection?.items as any || []);

  if (!isOpen && isModal) {
    return null;
  }

  const jsx = (
    <section
      className={cn(
        styles.wrapper,
        {
          [styles.wrapperModal]: isModal,
          [styles.wrapperStatic]: !isModal,
        },
      )}
      style={{ background: bgColor }}
    >
      <div className={cn({
        [styles.modal]: isModal,
        [styles.static]: !isModal,
      })}
      >
        {isModal && (
          <button
            type="button"
            className={styles.close}
            onClick={() => {
              if (closeHandler?.fullText) {
                try {
                // @ts-ignore
                // eslint-disable-next-line no-eval
                  const func = eval(`(${closeHandler.fullText})`);

                  return func(setIsOpen);
                } catch (error) {
                  console.error(error);
                }
              }

              return setIsOpen(false);
            }}
          >
            <Cross />
          </button>
        )}
        <HeadingSection block={parsedBlocks.headerBlock} dynamicData={dynamicData} />
        <ButtonSection block={parsedBlocks.buttonBlock} dynamicData={dynamicData} />
        <OfferDetailsSection block={parsedBlocks.offerDetailsBlock} dynamicData={dynamicData} />
        <LegalTermsSection block={parsedBlocks.legalTermsBlock} dynamicData={dynamicData} />
      </div>
    </section>
  );

  if (isModal) {
    return createPortal(jsx, document.body);
  }

  return jsx;
};

export default DynamicModalWithStore;
